import Element, { IElementLayout } from './Element';
import GameObject from '../../GameObject/GameObject';
import Button, { IButtonData } from './BaseElements/Button';
import Renderable from '../../Component/Renderable';
import { TextAlignment } from './TextAlignment';

export interface IUIElementLayout {
    anchors?: [number, number, number, number];
    pivot?: [number, number];
    offsets?: [number, number, number, number];
    scale?: [number, number];
    constrains?: IUIConstraints;
    rotation?: number;
    angle?: number;
}

export interface IUIConstraints {
    heightMin: number;
    heightMax: number;
    widthMin: number;
    widthMax: number;
}

export interface ITextStyle {
    font?: string;
    fill?: string;
    fontSize?: string;
    [key: string]: any;
}

export interface IImageData {
    name?: string;
    cacheKey?: string;
    layout?: IElementLayout;
    type?: ImageType;
    isMask?: boolean;
    alpha?: number;
    hidden?: boolean;
}

export enum ImageType {
    IMAGE = 'image',
    NINE_SLICE = 'nineslice',
}

export interface ITextData {
    name?: string;
    text?: string;
    textLocalizationKey?: string;
    layout?: IUIElementLayout;
    style?: ITextStyle;
    align?: TextAlignment;
    bounds?: [number, number];
    wordWrap?: boolean,
    wordWrapWidth?: number
}

export default class UIItem extends Element {
    public onCreate(params: any): void {}

    /**
     * @deprecated Use relative button instead (Will be renamed)
     * @param params.name - Name of Element
     * @param params.cacheKey - Image Key
     * @param params.textLocalizationKey - Text
     * @param params.hidden - Visible state of Element - (Defaults to false)
     * @param params.align - Alignment of Text in Button - (Defaults to 'center')
     * @param params.textOffset - Offset of Text in Button - (Optional)
     * @param params.layout - Layout of Button - (Optional)
     * @param params.enabled - Decideds if button is interactable - (Defaults to true)
     * @returns An Element with interactive capabilities
     */
    public static createButton(params: IButtonData): Button {
        const button = GameObject.Instantiate(Button, {
            type: 'group',
            name: params.name,
            layout: params.layout || {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                scale: [1, 1],
            },
            components: [
                {
                    type: 'image',
                    data: {
                        cacheKey: params.cacheKey,
                    },
                },
                {
                    type: 'input',
                    data: {
                        enabled:
                            params.enabled !== undefined
                                ? params.enabled
                                : true,
                        buttonMode: params.buttonMode ?? true,
                    },
                },
                {
                    type: 'visibility',
                    data: {
                        hidden:
                            params.hidden !== undefined ? params.hidden : false,
                    },
                },
            ],
        });

        if (params.textData) {
            const text = UIItem.createText(params.textData);

            button.addChild(text);
        }

        return button;
    }

    /**
     * Creates an image
     * @param params.name - Name of element
     * @param params.image - Image key
     * @param params.layout - Layout of image
     * @param params.type - Type of Image - defaults to normal sprite
     * @param params.isMask - Is the image a mask?
     */
    public static createImage(params: IImageData): Element {
        const imageData: any = {
            type: 'group',
            name: params.name,
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                scale: [1, 1],
                ...params.layout,
            },
            components: [
                {
                    type: 'image',
                    data: {
                        cacheKey: params.cacheKey,
                    },
                },
                {
                    type: 'visibility',
                    data: {
                        hidden: params.hidden,
                    },
                },
            ],
        };
        if (params.isMask) {
            imageData.components.push({
                type: 'mask',
                data: {
                    offset: [0, 0, 0, 0],
                },
            });
        }

        const image: Element = GameObject.Instantiate(Element, imageData);

        if (params.alpha) {
            image.getComponent(Renderable).alpha = params.alpha;
        }

        return image;
    }

    /**
     * @param params.name - Name of Element
     * @param params.layout - Layout of text
     * @param params.style - Style of text
     * @param params.textLocalizationKey - Text key
     * @param params.align - Alignment of Text - (Defaults to 'center')
     * @returns A Text Element
     */
    public static createText(params: ITextData, _bounds?: any): Element {
        const data = {
            type: 'text',
            name: params.name,
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                ...params.layout,
            },
            components: [
                {
                    type: 'text',
                    data: {
                        ...(params.textLocalizationKey
                            ? {
                                  textLocalizationKey:
                                      params.textLocalizationKey,
                              }
                            : { text: params.text || '' }),
                        style: params.style || {
                            font: 'RussoOne',
                            fill: '#006EB6',
                            fontSize: '32px',
                            align: params.align || 'center',
                        },
                        align: params.align || 'center',
                        bounds: _bounds || params.bounds || undefined,
                        wordWrap: params.wordWrap ? params.wordWrap : false,
                        wordWrapWidth: params.wordWrapWidth ? params.wordWrapWidth : undefined
                    },
                },
            ],
        };

        const text: Element = GameObject.Instantiate(Element, data);

        return text;
    }
}
